import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Stack } from "@mui/material";
import { isEqual, isNil, omitBy } from "lodash-es";
import { useCallback, useEffect, useMemo } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { toast } from "react-hot-toast";
import { uuidv4 } from "../../../utils/uuid";
import { useBrandKit } from "../../brand-kit/hooks/use-brand-kit";
import { EmailTemplateEditor } from "../../email-template-editor/email-template-editor";
import { FixedFooter } from "../../generic/components/fixed-footer";
import { useEmailDesignActions } from "../hooks/use-email-design";
import { EmailDesignNameTakenError, emailDesignSchema } from "../types/email-design-type";
import { emailHeaderSchema } from "../types/email-header-type";
import { ArchiveButton } from "./buttons/archive-button";
import { DuplicateButton } from "./buttons/duplicate-button";
import { SaveButton } from "./buttons/save-button";

import type { UUID } from "../../../utils/uuid";
import type { BrandKitLogo } from "../../brand-kit/server/brand-kit-service";
import type { EmailDesign as SupabaseEmailDesign } from "../server/supabase-email-design-service";
import type { EmailDesignSchema } from "../types/email-design-type";
import type { FC } from "react";

export const EmailDesign: FC<{
  onSave: (emailDesignId?: UUID) => void;
  emailDesign?: SupabaseEmailDesign;
  emailDesigns: SupabaseEmailDesign[];
  setIsDirty: (isDirty: boolean) => void;
  hasMaxTemplates: boolean;
  children: React.JSX.Element;
}> = ({ onSave, emailDesign, emailDesigns, hasMaxTemplates, setIsDirty, children }) => {
  const { branding } = useBrandKit();
  const {
    save: { loading: saving, load: doSave },
    archive: { loading: archiving, load: doArchive },
  } = useEmailDesignActions(emailDesign);

  const defaultValues: EmailDesignSchema = useMemo(
    () => makeDefaultTemplateValues(emailDesign, branding?.logo),
    [emailDesign, branding?.logo],
  );

  const methods = useForm({
    defaultValues,
    resolver: yupResolver(emailDesignSchema),
  });

  const { reset } = methods;

  useEffect(() => reset(defaultValues), [defaultValues, reset]);

  useEffect(() => setIsDirty(methods.formState.isDirty), [methods.formState.isDirty, setIsDirty]);

  const save = useCallback(
    (id: UUID, data: EmailDesignSchema): void => {
      void toast
        .promise(
          doSave(id, data),
          {
            loading: "Saving...",
            success: "Saved",
            error: (error: unknown) =>
              error instanceof EmailDesignNameTakenError ? error.message : "Error saving settings",
          },
          { position: "top-right" },
        )
        .then((x) => x && onSave(x.id))
        .catch(console.error);
    },
    [doSave, onSave],
  );

  const archive = useCallback(
    (new_id: UUID): void => {
      void toast
        .promise(
          doArchive(new_id),
          {
            loading: "Archiving...",
            success: "Archived",
            error: "Error archiving template",
          },
          { position: "top-right" },
        )
        .then(() => onSave(new_id))
        .catch(console.error);
    },
    [doArchive, onSave],
  );

  return (
    <FormProvider {...methods}>
      <EmailTemplateEditor mb={10}>{children}</EmailTemplateEditor>
      <FixedFooter>
        <Stack direction="row" justifyContent="flex-end" sx={{ py: 2 }} gap={2}>
          {emailDesign && emailDesigns.length > 1 && (
            <ArchiveButton
              archive={archive}
              archiving={archiving}
              emailDesign={emailDesign}
              allDesigns={emailDesigns}
              sx={{ bgcolor: "background.paper" }}
            />
          )}

          {!hasMaxTemplates && (
            <DuplicateButton
              disabled={saving}
              onClick={() =>
                save(uuidv4(), {
                  ...defaultValues,
                  name: `${defaultValues.name} (Duplicate)`,
                  is_default: false,
                })
              }
              sx={{ bgcolor: "background.paper" }}
            />
          )}
          <UndoButton defaultValues={defaultValues} />
          <SaveButton save={save} saving={saving} emailDesign={emailDesign} />
        </Stack>
      </FixedFooter>
    </FormProvider>
  );
};

const UndoButton: FC<{ defaultValues: EmailDesignSchema }> = ({ defaultValues }) => {
  const { reset, watch } = useFormContext<EmailDesignSchema>();

  const formValues = watch();

  return (
    <Button
      data-analytics-id="email-design-undo-changes"
      variant="outlined"
      color="warning"
      disabled={isEqual(formValues, defaultValues)}
      onClick={() => reset(defaultValues)}
      sx={{ bgcolor: "background.paper" }}
    >
      Undo Changes
    </Button>
  );
};

export function makeDefaultTemplateValues(
  emailDesign?: SupabaseEmailDesign,
  branding_logo?: BrandKitLogo,
): EmailDesignSchema {
  const defaultData = {
    ...emailDesignSchema.getDefault(),
    header: emailHeaderSchema.getDefault(),
  };

  const newHeader = branding_logo
    ? {
        header: {
          ...defaultData.header,
          logo: {
            src: branding_logo.src,
            align: "left" as const,
            proportionOfMaxWidth: 1,
            version: 2,
          },
          text: null,
        },
      }
    : {};

  return {
    ...defaultData,
    ...newHeader,
    ...omitBy(emailDesign ?? {}, isNil),
    id: emailDesign?.id ?? uuidv4(),
  };
}
